<template>
  <div class="container-nzidoo py-3 px-2 px-md-auto">

    <PageTitle :title="product?.name" />
    <!-- PRODUCT SUMMARY -->
    <div v-if="product" class="card-section product-summary">
      <div class="summary-top">
        <img v-if="Array.isArray(product.pictures) && product.pictures.length" :src="storageUrl + product.pictures[0]"
          class="summary-image" />
        <div class="summary-info">
          <p class="summary-price">
            {{ product.fixed_price || product.starting_price | currency(" DA", 0, { symbolOnLeft: false }) }}
          </p>
          <p class="summary-seller">
            {{ $t('attr.ordered_at') }} <strong>{{ product.user?.firstname }}</strong>
          </p>
          <p class="summary-seller">
            <strong>{{ product.description }}</strong>
          </p>
          <span class="status-badge" :class="{ done: productSelled(product), ongoing: !productSelled(product) }">
            {{ productSelled(product) ? $t("filter.completed") : $t("filter.inProgress") }}
          </span>
          <small class="summary-date">{{ formatDate(product.updated_at) }}</small>
        </div>
      </div>
    </div>


    <!-- DELIVERY INFO -->
    <div v-if="product.delivery_time || product.delivery_cost" class="card-section">
      <h4>{{ $t("sentence.delivery") }}</h4>
      <div class="delivery-details">
        <p v-if="product.delivery_time">
          <i class="mdi mdi-truck-fast"></i>
          {{ $t("attr.deliveryTime") }}: <strong>{{ product.delivery_time }}</strong>
        </p>
        <p v-if="product.delivery_cost">
          <i class="mdi mdi-truck"></i>
          {{ $t("attr.deliveryCost") }}:
          <strong>{{ product.delivery_cost | currency(" DA", 0, { symbolOnLeft: false }) }}</strong>
        </p>
        <p v-if="product.tracking_number">
          <i class="mdi mdi-barcode"></i>
          {{ $t("attr.trackingNumber") }}:
          <span class="tracking-number">{{ product.tracking_number }}</span>
        </p>
      </div>
    </div>

    <!-- 🟩 BID INFORMATION -->
    <div v-if="product.sales_type == 'auction'">
      <div v-if="product.bids && product.bids.length" class="card-section">
        <h4>{{ $t("sentence.bidInformation") }}</h4>
        <div class="bid-info">
          <div class="bid-line">
            <span>{{ $t("attr.startingPrice") }}</span>
            <span>{{ product.starting_price | currency(" DA", 0, { symbolOnLeft: false }) }}</span>
          </div>
          <div class="bid-line">
            <span>{{ $t("attr.highestBid") }}</span>
            <span class="highlight">
              {{ highestBid | currency(" DA", 0, { symbolOnLeft: false }) }}
            </span>
          </div>
          <div class="bid-line">
            <span>{{ $t("attr.totalBids") }}</span>
            <span>{{ product.bids.length }}</span>
          </div>
          <div v-if="userBid" class="bid-line">
            <span>{{ $t("sentence.yourBid") }}</span>
            <span>{{ userBid.amount | currency(" DA", 0, { symbolOnLeft: false }) }}</span>
          </div>
        </div>
      </div>

      <div v-else class="card-section">
        <h4>{{ $t("sentence.bidInformation") }}</h4>
        <p class="no-bid">{{ $t("sentence.noBidsYet") }}</p>
      </div>

    </div>
    <div v-else>
      <!-- 🟩 Offers INFORMATION -->
      <div v-if="product.offers && product.offers.length > 0" class="card-section">
        <h4>{{ $t("sentence.offersInformation") }}</h4>

        <div class="bid-info mb-3">

          <div class="bid-line">
            <span>{{ $t("sentence.highestOffer") }}</span>
            <span class="highlight">
              {{ highestOffer | currency(" DA", 0, { symbolOnLeft: false }) }}
            </span>
          </div>

          <div class="bid-line">
            <span>{{ $t("attr.totalBids") }}</span>
            <span>{{ product.offers.length }}</span>
          </div>

          <div v-if="userOffer" class="bid-line">
            <span>{{ $t("sentence.yourOffer") }}</span>
            <span>{{ userOffer.price | currency(" DA", 0, { symbolOnLeft: false }) }}</span>
          </div>
        </div>

        <!-- 🔥 OFFERS LIST -->
        <div class="offers-list">
          <h5 class="mb-3">{{ $t("attr.allOffers") }}</h5>

          <div class="offer-item" v-for="offer in product.offers" :key="offer.id">
            <div class="offer-left">
              <div class="offer-price">
                {{ offer.price | currency(" DA", 0, { symbolOnLeft: false }) }}
              </div>

              <div class="offer-meta">
                <span class="offer-user">
                  <i class="mdi mdi-account"></i>
                  {{ offer.user?.firstname || "-" }}
                </span>

                <span class="offer-date">
                  <i class="mdi mdi-calendar"></i>
                  {{ formatDate(offer.created_at) }}
                </span>
              </div>
            </div>

            <div class="offer-actions">
              <button class="btn-accept" @click="acceptOffer(offer)">
                <i class="mdi mdi-check"></i>
                {{ $t("action.accept") }}
              </button>

              <button class="btn-decline" @click="declineOffer(offer)">
                <i class="mdi mdi-close"></i>
                {{ $t("action.refuse") }}
              </button>
            </div>
          </div>
        </div>

      </div>

      <div v-else class="card-section">
        <h4>{{ $t("sentence.offersInformation") }}</h4>
        <p class="no-bid">{{ $t("sentence.noOffersYet") }}</p>
      </div>
    </div>


    <div class="row gap-4 px-3">
      <!-- PRICE DETAILS -->
      <div class="card-section col-md-4">
        <h4>{{ $t("sentence.priceDetails") }}</h4>
        <div class="price-details">
          <div class="price-line">
            <span>{{ $t("attr.price") }}</span>
            <span>{{ product.fixed_price | currency(" DA", 0, { symbolOnLeft: false }) }}</span>
          </div>
          <div v-if="product.delivery_cost" class="price-line">
            <span>{{ $t("attr.deliveryCost") }}</span>
            <span>{{ product.delivery_cost | currency(" DA", 0, { symbolOnLeft: false }) }}</span>
          </div>
          <div class="price-total">
            <span>{{ $t("attr.total") }}</span>
            <span class="total-amount">
              {{
                (Number(product.fixed_price || 0) + Number(product.delivery_cost || 0)) |
                currency(" DA", 0, { symbolOnLeft: false })
              }}
            </span>
          </div>
        </div>
      </div>

      <!-- TRANSACTION INFO -->
      <div class="card-section col-md-4">
        <h4>{{ $t("sentence.transactionDetails") }}</h4>
        <p>
          {{ $t("attr.transactionNumber") }} :
          <strong>{{ product.id }}</strong>
        </p>
      </div>

      <!-- SELLER INFO -->
      <div class="card-section seller-info col-md">
        <div class="seller-header">
          <div class="seller-avatar">
            {{ product.user?.firstname?.[0]?.toUpperCase() }}
          </div>
          <div class="seller-name">{{ product.user?.firstname }}</div>
        </div>
        <router-link :to="{ name: 'messenger', params: { id: product.user?.id } }" class="btn-contact">
          <i class="mdi mdi-email"></i>
          {{ $t("action.message") }}
        </router-link>

      </div>
    </div>
    <div class="py-4 pb-md-0"></div>
    <div class="pb-2 pb-md-0"></div>
  </div>
</template>

<script>
import config from "@/config";
import helper from "@/services/helper";
import PageTitle from '@/components/templates/PageTitle.vue';

export default {
  components: { PageTitle },
  name: "ProductDetails",
  data() {
    return {
      product: null,
      highestOffer: 0,
      storageUrl: config.storageUrl,
      userBid: null,
    };
  },
  computed: {
    highestBid() {
      if (!this.product?.bids?.length) return 0;
      return Math.max(...this.product.bids.map((b) => b.amount));
    },
  },
  created() {
    const id=this.$route.params.id;
    this.fetchProduct(id);
  },
  methods: {
    fetchProduct(id) {
      this.$axios
        .get(`product/get/${id}`)
        .then((res) => {
          this.product=res.data;
          this.findUserBid();
          this.product.offers.sort((a, b) => Number(b.price)-Number(a.price));
          this.highestOffer= this.product.offers[0].price
        })
        .catch(() => { });
    },
    findUserBid() {
      const userId=this.$store?.state?.user?.id;
      if (!userId||!this.product?.bids?.length) return;
      this.userBid=this.product.bids.find((b) => b.user_id===userId)||null;
    },
    productSelled(product) {
      if (!product) return false;
      const bidWon=product.bids?.some((b) => b.status===1);
      const offerWon=product.offers?.some((o) => o.status===1);
      return bidWon||offerWon;
    },
    acceptOffer(offer) {
      // handle accept
      console.log("Accepted", offer);
    },

    declineOffer(offer) {
      // handle decline
      console.log("Declined", offer);
    },
    formatDate: helper.formatDate,
  },
};
</script>

<style scoped>
p {
  line-height: 1.1rem;
}



.btn-back {
  background: none;
  border: none;
  color: var(--color-nz);
  font-size: 1.2rem;
  cursor: pointer;
}


/* CARD BASE */


/* PRODUCT SUMMARY */
.summary-top {
  display: flex;
  align-items: stretch;
  gap: 1rem;
}

.summary-image {
  width: 100px;
  /* fixed width */
  border-radius: 10px;
  object-fit: cover;
  /* ensures image fills space nicely */
  flex-shrink: 0;
  /* keeps width fixed */
  align-self: stretch;
  /* ensures image fills full height of .summary-top */
}

.summary-price {
  font-weight: 700;
  font-size: 1.2rem;
}

.status-badge {
  display: inline-block;
  margin-top: 0.4rem;
  padding: 0.3rem 0.6rem;
  border-radius: 8px;
  font-size: 0.8rem;
  font-weight: 600;
}

.status-badge.done {
  background: #e6f8ea;
  color: var(--success);
}

.status-badge.ongoing {
  background: var(--warning-light);
  color: var(--warning);
}

.summary-date {
  display: block;
  color: var(--color-9);
  font-size: 0.8rem;
}

/* DELIVERY INFO */
.delivery-details p {
  margin: 0.5rem 0;
  display: flex;
  align-items: center;
  gap: 0.4rem;
}

.tracking-number {
  background: #f0f0f0;
  padding: 0.2rem 0.5rem;
  border-radius: 4px;
  font-family: monospace;
}

/* BID INFO */
.bid-info {
  margin-top: 0.5rem;
}

.bid-line {
  display: flex;
  justify-content: space-between;
  margin: 0.3rem 0;
}

.bid-line .highlight {
  color: var(--color-nz);
  font-weight: 700;
}

.no-bid {
  color: var(--color-9);
  font-style: italic;
}

/* PRICE DETAILS */
.price-line,
.price-total {
  display: flex;
  justify-content: space-between;
  margin: 0.3rem 0;
}

.total-amount {
  font-weight: bold;
  color: var(--success);
}

/* SELLER INFO */
.seller-header {
  display: flex;
  align-items: center;
  gap: 0.7rem;
  margin-bottom: 0.8rem;
}

.seller-avatar {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: var(--color-nz);
  color: white;
  font-weight: bold;
  display: flex;
  align-items: center;
  justify-content: center;
}

.btn-contact {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 0.4rem;
  width: 100%;
  background: #f0f7ff;
  color: var(--color-nz);
  padding: 0.7rem;
  border-radius: 10px;
  text-decoration: none;
  font-weight: 600;
}

.btn-contact:hover {
  background: #e6f0ff;
}

/* OFFERS LIST */
.offers-list {
  margin-top: 1rem;
}

.offer-item {
  display: flex;
  justify-content: space-between;
  align-items: stretch;
  background: #fafafa;
  border-radius: 12px;
  padding: 1rem;
  margin-bottom: 0.8rem;
  border: 1px solid var(--color-e);
  gap: 1rem;
  transition: 0.2s;
}

.offer-item:hover {
  background: #f5f9ff;
  border-color: #d7e8ff;
}

/* LEFT INFO */
.offer-left {
  flex: 1;
}

.offer-price {
  font-size: 1.1rem;
  font-weight: 700;
  color: var(--color-nz);
}

.offer-meta {
  margin-top: 0.3rem;
  display: flex;
  flex-wrap: wrap;
  gap: 0.6rem;
  color: var(--color-7);
  font-size: 0.85rem;
}

.offer-meta i {
  font-size: 1rem;
  color: #4090ff;
}

.offer-user,
.offer-date {
  display: flex;
  align-items: center;
  gap: 0.3rem;
}

/* ACTION BUTTONS */
.offer-actions {
  display: flex;
  justify-content: center;
  gap: 0.5rem;
  min-width: 110px;
}

.btn-accept,
.btn-decline {
  padding: 0.45rem 0.7rem;
  border-radius: 8px;
  font-size: 0.85rem;
  font-weight: 600;
  border: none;
  cursor: pointer;
  width: 100%;
  display: flex;
  align-items: center;
  gap: 0.3rem;
  justify-content: center;
  transition: 0.2s;
}

.btn-accept {
  background: #e8f8ec;
  color: var(--success);
}

.btn-accept:hover {
  background: #d3f1dc;
}

.btn-decline {
  background: #fdecea;
  color: #d9534f;
}

.btn-decline:hover {
  background: #f8d7da;
}


/* RESPONSIVE */
@media (min-width: 992px) {


  .summary-top {
    gap: 2rem;
  }

  .summary-image {
    width: 80px;
    height: 120px;
  }
}

/* RESPONSIVE MOBILE */
@media (max-width: 768px) {
  .offer-item {
    flex-direction: column;
    text-align: left;
  }

  .offer-actions {
    justify-content: space-between;
    min-width: 100%;
  }

  .btn-accept,
  .btn-decline {
    flex: 1;
  }
}
</style>
